import { useEffect, useRef } from "react";

/**
 * La planimetria stilizzata, portata da `PlantDiagram.qml`.
 *
 * E' il ripiego di quando l'impianto non ha una foto caricata da Settings:
 * vessel, colonna di distillazione, separatore, torcia, tubazioni, valvole e
 * pompa disegnati su un viewport 1000x600 e scalati sul riquadro.
 *
 * **Le ellissi sono disegnate a rettangolo di contenimento**, non a centro +
 * raggi. Il `.qml` chiama `ctx.ellipse()` con la firma del canvas del browser
 * (cx, cy, rx, ry, ...), ma il Context2D di Qt ne accetta quattro argomenti e
 * li legge come `x, y, w, h`: cupole e fiamma dell'app desktop stanno dove le
 * mette QUELLA lettura. Passando gli stessi numeri alla firma del browser il
 * disegno verrebbe diverso da quello che l'operatore ha davanti oggi, ed e'
 * una differenza che si vede: la fiamma si sposterebbe rispetto al camino.
 */
export function PlantDiagram() {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;

    const draw = () => {
      const dpr = globalThis.devicePixelRatio || 1;
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      if (w <= 0 || h <= 0) return;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      paint(ctx, w, h);
    };

    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(parent);
    return () => ro.disconnect();
  }, []);

  return <canvas className="plantdiag" ref={ref} />;
}

/** L'ellisse come la disegna Qt: rettangolo di contenimento. */
function boxEllipse(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
}

function paint(ctx: CanvasRenderingContext2D, width: number, height: number) {
  // Scala dal viewport 1000x600 alle dimensioni reali
  ctx.save();
  ctx.scale(width / 1000, height / 600);

  // -- Ground --------------------------------------------------------
  ctx.fillStyle = "rgba(255,255,255,0.03)";
  ctx.fillRect(0, 480, 1000, 120);
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, 480);
  ctx.lineTo(1000, 480);
  ctx.stroke();

  // -- Left: horizontal vessel V-101 ---------------------------------
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  ctx.fillRect(100, 380, 8, 100);
  ctx.fillRect(250, 380, 8, 100);
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(115, 320);
  ctx.lineTo(265, 320);
  ctx.lineTo(265, 390);
  ctx.lineTo(280, 390);
  ctx.arc(280, 355, 35, Math.PI / 2, -Math.PI / 2, true);
  ctx.lineTo(115, 320);
  ctx.arc(115, 355, 35, -Math.PI / 2, Math.PI / 2, true);
  ctx.closePath();
  ctx.fillStyle = "rgba(255,255,255,0.07)";
  ctx.fill();
  ctx.stroke();
  // Manhole
  ctx.beginPath();
  ctx.arc(180, 320, 12, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.06)";
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(180, 320, 6, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.03)";
  ctx.fill();
  // Pressure gauge
  ctx.beginPath();
  ctx.arc(120, 320, 8, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.03)";
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.18)";
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.strokeStyle = "#60a5fa";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(120, 320);
  ctx.lineTo(120, 314);
  ctx.stroke();
  // Label
  ctx.fillStyle = "rgba(255,255,255,0.12)";
  ctx.font = "10px monospace";
  ctx.textAlign = "center";
  ctx.fillText("V-101", 180, 370);

  // -- Center: distillation column C-201 -----------------------------
  ctx.fillStyle = "rgba(255,255,255,0.07)";
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(450, 140);
  ctx.arc(450, 140, 30, 0, Math.PI, false);
  ctx.lineTo(420, 480);
  ctx.arc(450, 480, 30, Math.PI, 0, false);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // Column highlight
  ctx.fillStyle = "rgba(255,255,255,0.06)";
  ctx.fillRect(435, 155, 12, 310);
  // Trays
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 3]);
  for (const ty of [190, 240, 290, 340, 390]) {
    ctx.beginPath();
    ctx.moveTo(425, ty);
    ctx.lineTo(475, ty);
    ctx.stroke();
  }
  ctx.setLineDash([]);
  // Top dome
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  boxEllipse(ctx, 450, 145, 30, 10);
  ctx.fill();
  ctx.stroke();
  // Base
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  ctx.fillRect(415, 470, 70, 10);
  ctx.fillStyle = "rgba(255,255,255,0.12)";
  ctx.font = "10px monospace";
  ctx.textAlign = "center";
  ctx.fillText("C-201", 450, 500);

  // -- Right: vertical vessel D-301 ----------------------------------
  ctx.fillStyle = "rgba(255,255,255,0.07)";
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(665, 260);
  ctx.arc(665, 285, 25, Math.PI * 1.5, Math.PI * 0.5, false);
  ctx.lineTo(640, 390);
  ctx.arc(665, 390, 25, Math.PI * 0.5, Math.PI * 1.5, false);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // Highlight
  ctx.fillStyle = "rgba(255,255,255,0.06)";
  ctx.fillRect(653, 275, 10, 100);
  // Dome
  ctx.beginPath();
  boxEllipse(ctx, 665, 265, 25, 8);
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  ctx.fill();
  ctx.stroke();
  // Level indicator
  ctx.fillStyle = "rgba(255,255,255,0.03)";
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 1;
  ctx.strokeRect(695, 310, 6, 50);
  ctx.fillStyle = "#22c55e";
  ctx.globalAlpha = 0.4;
  ctx.fillRect(696, 335, 4, 24);
  ctx.globalAlpha = 1;
  // Base + legs
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  ctx.fillRect(635, 385, 60, 8);
  ctx.fillRect(645, 393, 6, 87);
  ctx.fillRect(680, 393, 6, 87);
  ctx.fillStyle = "rgba(255,255,255,0.12)";
  ctx.textAlign = "center";
  ctx.fillText("D-301", 665, 500);

  // -- Flare stack ---------------------------------------------------
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  ctx.fillRect(855, 180, 12, 300);
  ctx.fillStyle = "rgba(255,255,255,0.03)";
  ctx.fillRect(859, 180, 4, 300);
  // Braces
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(861, 480);
  ctx.lineTo(830, 480);
  ctx.stroke();
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(861, 400);
  ctx.lineTo(838, 480);
  ctx.stroke();
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(861, 320);
  ctx.lineTo(845, 480);
  ctx.stroke();
  // Flame
  const flareGrad = ctx.createLinearGradient(861, 195, 861, 170);
  flareGrad.addColorStop(0, "#ff6600");
  flareGrad.addColorStop(0.5, "#ff9933");
  flareGrad.addColorStop(1, "#ffcc00");
  ctx.globalAlpha = 0.8;
  ctx.fillStyle = flareGrad;
  ctx.beginPath();
  boxEllipse(ctx, 861, 170, 14, 25);
  ctx.fill();
  ctx.globalAlpha = 0.6;
  ctx.fillStyle = "#ffee88";
  ctx.beginPath();
  boxEllipse(ctx, 861, 160, 8, 15);
  ctx.fill();
  ctx.globalAlpha = 1;

  // -- Piping --------------------------------------------------------
  // Il gradiente e' alto un pixel: sotto y=1 vale l'ultimo stop, ed e'
  // esattamente cosi' che si vede nell'app desktop.
  const pipeGrad = ctx.createLinearGradient(0, 0, 0, 1);
  pipeGrad.addColorStop(0, "rgba(255,255,255,0.12)");
  pipeGrad.addColorStop(0.5, "rgba(255,255,255,0.18)");
  pipeGrad.addColorStop(1, "rgba(255,255,255,0.10)");
  ctx.strokeStyle = pipeGrad;
  ctx.lineWidth = 6;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(280, 355);
  ctx.lineTo(420, 355);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(480, 400);
  ctx.lineTo(640, 400);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(480, 180);
  ctx.lineTo(600, 180);
  ctx.arc(620, 200, 20, Math.PI * 1.5, 0, false);
  ctx.lineTo(640, 355);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(480, 300);
  ctx.lineTo(530, 300);
  ctx.arc(545, 315, 15, Math.PI * 1.5, Math.PI * 0.5, false);
  ctx.lineTo(545, 470);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(690, 330);
  ctx.lineTo(800, 330);
  ctx.arc(820, 310, 20, Math.PI * 0.5, 0, true);
  ctx.lineTo(855, 200);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(40, 355);
  ctx.lineTo(80, 355);
  ctx.stroke();
  // Thin pipes
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(545, 470);
  ctx.lineTo(700, 470);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(130, 390);
  ctx.lineTo(130, 470);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(240, 390);
  ctx.lineTo(240, 440);
  ctx.lineTo(350, 440);
  ctx.stroke();

  // -- Valves --------------------------------------------------------
  const valves: [number, number][] = [
    [350, 355],
    [530, 400],
    [600, 180],
    [800, 330],
    [350, 440],
  ];
  for (const [vx, vy] of valves) {
    ctx.fillStyle = "rgba(255,255,255,0.08)";
    ctx.strokeStyle = "rgba(255,255,255,0.18)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(vx - 8, vy - 6);
    ctx.lineTo(vx, vy);
    ctx.lineTo(vx - 8, vy + 6);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(vx + 8, vy - 6);
    ctx.lineTo(vx, vy);
    ctx.lineTo(vx + 8, vy + 6);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.strokeStyle = "rgba(255,255,255,0.18)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(vx, vy - 10);
    ctx.lineTo(vx, vy - 6);
    ctx.stroke();
    ctx.fillStyle = "rgba(255,255,255,0.12)";
    ctx.fillRect(vx - 4, vy - 14, 8, 4);
  }

  // -- Pump P-101 ----------------------------------------------------
  ctx.beginPath();
  ctx.arc(350, 470, 15, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.06)";
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(350, 470, 8, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.03)";
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = "rgba(255,255,255,0.12)";
  ctx.textAlign = "center";
  ctx.fillText("P-101", 350, 500);

  // -- Grid overlay --------------------------------------------------
  ctx.globalAlpha = 0.02;
  ctx.strokeStyle = "white";
  ctx.lineWidth = 0.5;
  for (let gx = 100; gx <= 900; gx += 100) {
    ctx.beginPath();
    ctx.moveTo(gx, 0);
    ctx.lineTo(gx, 600);
    ctx.stroke();
  }
  for (let gy = 100; gy <= 500; gy += 100) {
    ctx.beginPath();
    ctx.moveTo(0, gy);
    ctx.lineTo(1000, gy);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  ctx.restore();
}
